<template>
  <dp-seite titel="Prüfen & veröffentlichen" untertitel="Gespeicherter Entwurf → Vorschau → Freigabe → Sollzeiten der Zeitkontrolle" :snack="snack">
    <template #aktionen>
      <v-btn v-if="settings.can_approve" small text color="error" :disabled="!dp.plan" @click="$refs.zuruecknehmen.open()"><v-icon small left>mdi-undo-variant</v-icon>Planung zurücknehmen</v-btn>
      <v-btn small depressed color="warning" :disabled="!kannVorschau" :loading="busy" @click="vorschau"><v-icon small left>mdi-magnify-scan</v-icon>Vorschau erstellen</v-btn>
    </template>
    <template v-if="dp.plan">
      <div class="d-flex flex-wrap align-center mb-3" style="gap: 12px">
        <v-select v-model="vonWoche" :items="wochenItems" outlined dense hide-details label="Von Kalenderwoche" style="max-width: 250px"/>
        <v-select v-model="bisWoche" :items="wochenItems.filter(w => w.value >= vonWoche)" outlined dense hide-details label="Bis Kalenderwoche" style="max-width: 250px"/>
        <v-chip small outlined :color="!analyse ? '' : analyse.fehler || probleme.length ? 'orange darken-2' : 'success'">
          {{ analyse ? (analyse.fehler + probleme.length) + ' Hinweise' : 'wird geprüft …' }}
        </v-chip>
        <v-spacer/>
        <span class="text-caption grey--text">Weitere Wochen: Zeitraum in der Musterzuordnung oder im Kalender ändern.</span>
      </div>

      <v-alert v-if="dirty" type="warning" dense outlined>
        {{ dirty }} vorgemerkte Änderung(en) sind nicht gespeichert. Bitte erst speichern oder verwerfen – geprüft und veröffentlicht wird nur der gespeicherte Entwurf.
        <v-btn x-small text color="primary" @click="$router.push('/dienstplanung/zuordnung#schritt-5')">Zur Speicherung</v-btn>
      </v-alert>

      <div class="dp-metrics">
        <div><b>{{ analyse ? analyse.mitarbeiter_wochen.length : '–' }}</b>Mitarbeiter-Wochen geplant</div>
        <div><b>{{ analyse ? analyse.fehler : '–' }}</b>Tage mit Fehlern</div>
        <div><b>{{ probleme.length }}</b>unbesetzte Maschinentage</div>
        <div><b>{{ analyse ? analyse.aenderungen : '–' }}</b>Tage weichen von der Zeitkontrolle ab</div>
      </div>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-card outlined class="pa-3 fill-height">
            <div class="text-subtitle-2 mb-2">Im Planer geprüft</div>
            <div class="check"><v-icon small color="success">mdi-check</v-icon>Genau eine Wochenquelle je Mitarbeiter und ISO-Woche</div>
            <div class="check"><v-icon small :color="analyse && !analyse.fehler ? 'success' : 'orange darken-2'">{{ analyse && !analyse.fehler ? 'mdi-check' : 'mdi-alert' }}</v-icon>Musterwoche, Phase, Pool-Berechtigung, Lücken und Rastertage</div>
            <div class="check"><v-icon small :color="!probleme.length ? 'success' : 'orange darken-2'">{{ !probleme.length ? 'mdi-check' : 'mdi-alert' }}</v-icon>Abdeckung der Maschinentage durch Fahrerpositionen</div>
            <div class="check"><v-icon small :color="dp.regionValid ? 'success' : 'error'">{{ dp.regionValid ? 'mdi-check' : 'mdi-close' }}</v-icon>Gesetzliche Feiertage ({{ dp.region || 'Region fehlt' }}) – Soll 0 wie in der Zeitkontrolle</div>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-card outlined class="pa-3 fill-height">
            <div class="text-subtitle-2 mb-2">Beim Veröffentlichen</div>
            <div class="check"><v-icon small>mdi-lock-outline</v-icon>Nur mit Freigabeberechtigung; die Vorschau muss zum aktuellen Entwurf passen</div>
            <div class="check"><v-icon small>mdi-calendar-lock</v-icon>Bereits bestätigte Tage werden nicht geändert (gesperrt)</div>
            <div class="check"><v-icon small>mdi-table-edit</v-icon>Nur Soll-Felder: Sollzeit, Arbeitszeit, Pausen, Wertigkeit, Schicht</div>
            <div class="check"><v-icon small>mdi-refresh</v-icon>Anschließend Neuberechnung der betroffenen Tage in der Zeitkontrolle</div>
          </v-card>
        </v-col>
      </v-row>

      <h3 class="text-subtitle-2 mt-4 mb-1">Hinweise, die eine Veröffentlichung verhindern</h3>
      <v-simple-table dense class="dp-table">
        <thead><tr><th>Betrifft</th><th>Woche</th><th>Hinweis</th><th></th></tr></thead>
        <tbody>
        <tr v-for="(h, i) in hinweise" :key="i">
          <td>{{ h.wer }}</td><td>{{ h.woche }}</td><td class="red--text">{{ h.text }}</td>
          <td><v-btn x-small text color="primary" @click="$router.push(h.link)">Beheben</v-btn></td>
        </tr>
        <tr v-if="analyse && !hinweise.length"><td colspan="4" class="success--text"><v-icon small color="success">mdi-check-circle</v-icon> Keine Hinweise – der Zeitraum kann geprüft und veröffentlicht werden.</td></tr>
        </tbody>
      </v-simple-table>

      <h3 class="text-subtitle-2 mt-4 mb-1">Wirksame Mitarbeiter-Wochen</h3>
      <v-data-table dense :headers="headers" :items="analyse ? analyse.mitarbeiter_wochen : []" :items-per-page="25" no-data-text="Im Zeitraum ist niemand im Planer eingeplant." sort-by="iso_woche">
        <template v-slot:item.mita_bis_id="{ item }">{{ name(item.mita_bis_id) }}</template>
        <template v-slot:item.quelle="{ item }">{{ quelleLabel(effBesetzung(item.mita_bis_id, item.iso_woche)) }}</template>
        <template v-slot:item.stunden="{ item }">{{ hhmm(item.stunden) }}</template>
        <template v-slot:item.status="{ item }"><v-chip x-small outlined :color="status(item).color">{{ status(item).text }}</v-chip></template>
      </v-data-table>
    </template>

    <vorschau-dialog ref="vorschau" :can-approve="!!settings.can_approve" @published="veroeffentlicht" @error="(res, text) => toast(text, 'red accent-2')"/>
    <bestaetigen-dialog ref="zuruecknehmen" titel="Planung zurücknehmen?" ok-text="Zurücknehmen" ok-farbe="error" :busy="busy"
                        pflicht-text="Ich verstehe, dass alle Wochenquellen und Tagesvertretungen meines Planungsbereichs in diesen Wochen entfernt werden."
                        @ok="zuruecknehmen">
      <p class="text-body-2">Für {{ rangeText }} werden die Wochenquellen und Tagesvertretungen entfernt (protokolliert, nicht gelöscht im Protokoll).
        Danach „Vorschau erstellen“ und veröffentlichen: Die betroffenen Tage erhalten wieder ihre aktuell gültige Sollzeit (nur Soll-Felder).
        Musterwochen von Kolonnen und Maschinen bleiben erhalten.</p>
    </bestaetigen-dialog>
  </dp-seite>
</template>

<script>
import {dpApi, ERROR_CODES, errorText, fmtDay, fmtHHMM, periodWeeks, weekInfo, weekLabel} from '@/shared/dienstplan_api'
import seite from './seite'
import {dirtyCount, dp, effBesetzung, gruppeName, loadPlan, name, position, quelleLabel} from './store'
import DpSeite from './components/DpSeite'
import BestaetigenDialog from './components/BestaetigenDialog'
import VorschauDialog from './components/VorschauDialog'

export default {
  name: 'DpVeroeffentlichen',
  mixins: [seite],
  components: {DpSeite, BestaetigenDialog, VorschauDialog},
  data: () => ({
    vonWoche: '', bisWoche: '', analyse: null, busy: false,
    headers: [
      {text: 'Mitarbeiter', value: 'mita_bis_id'},
      {text: 'KW', value: 'iso_woche'},
      {text: 'Wochenquelle', value: 'quelle', sortable: false},
      {text: 'Soll / Woche', value: 'stunden'},
      {text: 'Zeitkontrolle', value: 'status', sortable: false},
    ],
  }),
  computed: {
    dirty: () => dirtyCount(),
    wochen () {
      return periodWeeks(dp.range.von, dp.range.bis)
    },
    wochenItems () {
      return this.wochen.map(w => ({value: w.key, text: weekLabel(w)}))
    },
    von () {
      const w = weekInfo(this.vonWoche)
      return w ? w.montag : ''
    },
    bis () {
      const w = weekInfo(this.bisWoche)
      return w ? w.sonntag : ''
    },
    rangeText () {
      return fmtDay(this.von) + ' – ' + fmtDay(this.bis)
    },
    probleme () {
      return this.analyse ? this.analyse.probleme.filter(p => p.code === 'POSITION_VACANT') : []
    },
    kannVorschau () {
      return this.settings.can_edit && !this.dirty && dp.regionValid && this.von && this.bis && !this.busy
    },
    hinweise () {
      if (!this.analyse) return []
      const res = []
      for (const w of this.analyse.mitarbeiter_wochen) {
        for (const f of w.fehler) {
          res.push({wer: name(w.mita_bis_id), woche: w.iso_woche, text: ERROR_CODES[f] || f, link: {path: '/dienstplanung/besetzung', query: {woche: w.iso_woche}}})
        }
      }
      for (const p of this.probleme) {
        res.push({wer: gruppeName(p.planungsgruppe_id), woche: p.iso_woche, text: fmtDay(p.datum) + ': ' + ((position(p.fahrerposition_id) || {}).bezeichnung || 'Fahrerposition') + ' unbesetzt',
          link: {path: '/dienstplanung/zuordnung', query: {typ: 'MACHINE', ziel: p.planungsgruppe_id, woche: p.iso_woche, schritt: 4}}})
      }
      if (this.analyse.probleme.some(p => p.code === 'REGION_INVALID')) res.unshift({wer: 'Mandant', woche: '', text: ERROR_CODES.REGION_INVALID, link: {path: '/dienstplanung/uebersicht'}})
      return res
    },
  },
  watch: {
    wochen: {
      immediate: true,
      handler (ws) {
        if (!ws.length) return
        if (!ws.some(w => w.key === this.vonWoche)) this.vonWoche = ws[0].key
        if (!ws.some(w => w.key === this.bisWoche) || this.bisWoche < this.vonWoche) this.bisWoche = ws[0].key
      },
    },
    vonWoche () {
      if (this.bisWoche < this.vonWoche) this.bisWoche = this.vonWoche
      this.analysieren()
    },
    bisWoche () {
      this.analysieren()
    },
  },
  created () {
    this.ensure().then(() => this.analysieren())
  },
  methods: {
    name,
    quelleLabel,
    effBesetzung,
    hhmm: fmtHHMM,
    analysieren () {
      if (!this.von || !this.bis || !dp.plan) return
      this.analyse = null
      dpApi.analyse(this.von, this.bis).then(res => { this.analyse = res }, res => this.fail(res))
    },
    status (a) {
      if (a.fehler.length) return {text: 'Fehler', color: 'error'}
      if (a.aktionen.INSERT || a.aktionen.UPDATE) return {text: 'wird geändert', color: 'primary'}
      if (a.aktionen.LOCKED) return {text: 'teils bestätigt', color: 'orange darken-2'}
      return {text: 'unverändert', color: 'success'}
    },
    vorschau () {
      this.busy = true
      dpApi.vorschau(this.von, this.bis).then(res => {
        this.busy = false
        this.$refs.vorschau.open(res.lauf, res.zeilen, res.probleme, true)
      }, res => {
        this.busy = false
        this.fail(res)
      })
    },
    veroeffentlicht (lauf) {
      this.toast('Veröffentlicht: ' + lauf.anzahl_neu + ' neu, ' + lauf.anzahl_geaendert + ' geändert, ' + lauf.anzahl_unveraendert + ' unverändert' +
        (lauf.anzahl_gesperrt ? ', ' + lauf.anzahl_gesperrt + ' gesperrt' : '') + '. Die Zeitkontrolle wird neu berechnet.')
      this.analysieren()
    },
    zuruecknehmen () {
      this.busy = true
      dpApi.zuruecknehmen(this.von, this.bis, dp.plan.revision).then(res => {
        this.busy = false
        this.$refs.zuruecknehmen.close()
        return loadPlan().then(() => {
          this.analysieren()
          this.toast(res.anzahl + ' Einträge zurückgenommen. Jetzt Vorschau erstellen und veröffentlichen.')
        })
      }, res => {
        this.busy = false
        this.toast(errorText(res), 'red accent-2')
      })
    },
  },
}
</script>

<style scoped>
.dp-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}
.dp-metrics div {
  border: 1px solid #dfe3e8;
  border-top: 3px solid #7ca9d0;
  background: #fff;
  border-radius: 3px;
  padding: 10px 14px;
  font-size: 12px;
  color: #607d8b;
}
.dp-metrics b {
  display: block;
  font-size: 24px;
  font-weight: 500;
  color: #263238;
}
.check {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13px;
  padding: 5px 0;
  border-bottom: 1px solid #f0f0f0;
}
</style>
